import { useEffect, useState } from "react";
import PoiApi from "../../poiApi";
import { useAppSelector } from "app/hooks";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import { PoiDeletePayload } from "../../poiModels";

type UsePoiDelete = () => [
  modalOpen: boolean,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  loading: boolean,
  actionCallback: () => Promise<void>
];

/**
 * # Delete a POI
 */
export const usePoiDelete: UsePoiDelete = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [scoutPointId, setScoutPointId] = useState<
    PoiDeletePayload["scoutPointId"] | null
  >(null);
  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.fid
  );

  // This listener is for when krpano requests for a POI to be deleted
  useEffect(() => {
    const deletePoiHandler = async (
      event: WindowEventMap["krpano__poi_delete"]
    ) => {
      if (
        notNullNorUndefined(event.detail) &&
        notNullNorUndefined(event.detail.poiName)
      ) {
        // Extract the scout point ID from the poiName
        // Assuming poiName is formatted as `poi ${scoutPointId}`
        const [, targetPoi] = event.detail.poiName.split(" ");
        setScoutPointId(targetPoi);
        setModalOpen(true);
      } else {
        // TODO: KrpanoDispatchedEvent Error handling
      }
    };
    window.addEventListener("krpano__poi_delete", deletePoiHandler);
    return () => {
      window.removeEventListener("krpano__poi_delete", deletePoiHandler);
    };
  }, []);

  const poiDelete = async () => {
    if (
      notNullNorUndefined(scoutPointId) &&
      notNullNorUndefined(currentFieldId) &&
      notNullNorUndefined(guid)
    ) {
      setLoading(true);
      const response = await PoiApi.deletePoi({
        scoutPointId: scoutPointId,
        fieldId: currentFieldId,
        panoGuid: guid,
      });
      setLoading(false);
      if (response.success && response.data) {
        // Remove POI
        window.krpano.removehotspot(`poi ${scoutPointId}`);
        // TODO: Success handling
        // toast.success("POI deleted");
        // Close modal
        setModalOpen(false);
      } else {
        // TODO: Error handling
        console.warn("Error deleting POI", response.errors);
      }
    } else {
      console.warn("Possible missing data, check if any of these are null", {
        scoutPointId,
        currentFieldId,
        guid,
      });
    }
  };

  return [modalOpen, setModalOpen, loading, poiDelete];
};
